import { useEffect, useRef } from "react";
import { FEATURED_TIKTOK } from "@/lib/siteAssets";

/**
 * Official TikTok embed — loads embed.js on mount and renders the
 * blockquote TikTok looks for. Reasonably lightweight; the script itself is
 * cached by TikTok CDN.
 */
export default function TikTokEmbed({ url, videoId, username, caption }) {
    const embedUrl = url || FEATURED_TIKTOK.url;
    const embedId = videoId || FEATURED_TIKTOK.videoId;
    const embedUser = username || FEATURED_TIKTOK.username;
    const containerRef = useRef(null);

    useEffect(() => {
        // Load the embed script once (idempotent — TikTok's script handles re-runs)
        const existing = document.querySelector('script[src*="tiktok.com/embed.js"]');
        if (existing) {
            // If script already loaded, ask TikTok to re-process the DOM
            if (window.tiktokEmbed) {
                try {
                    window.tiktokEmbed.lib.render(containerRef.current);
                } catch (e) {
                    /* ignore */
                }
            }
            return;
        }
        const s = document.createElement("script");
        s.src = "https://www.tiktok.com/embed.js";
        s.async = true;
        document.body.appendChild(s);
    }, []);

    return (
        <div
            ref={containerRef}
            className="w-full flex flex-col items-center"
            data-testid="tiktok-embed"
        >
            <blockquote
                className="tiktok-embed"
                cite={embedUrl}
                data-video-id={embedId}
                style={{ maxWidth: "605px", minWidth: "280px", width: "100%" }}
            >
                <section>
                    <a
                        target="_blank"
                        rel="noopener noreferrer"
                        title={`@${embedUser}`}
                        href={`https://www.tiktok.com/@${embedUser}`}
                    >
                        @{embedUser}
                    </a>
                </section>
            </blockquote>
            {caption && (
                <p className="mt-3 text-xs text-neutral-500 text-center max-w-sm leading-relaxed">
                    {caption}
                </p>
            )}
        </div>
    );
}
